import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { serviceOptions as fbServiceOptions } from '../data/mock';
import { useContent } from '../context/ContentContext';
import { CheckCircle2, Send } from 'lucide-react';
import { saveSubmission } from '../lib/store';

const ContactForm = ({ variant = 'default', storeKey = 'contacts' }) => {
  const { serviceOptions = fbServiceOptions } = useContent();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    saveSubmission(storeKey, { ...form, date: new Date().toISOString() });
    setSubmitted(true);
    toast({ title: 'Message Sent!', description: 'Thanks for reaching out. We will get back to you soon.' });
    setTimeout(() => setSubmitted(false), 3000);
    setForm({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const dark = variant === 'onImage';
  const labelCls = dark ? 'text-white/90' : 'text-slate-700';

  if (submitted) {
    return (
      <div className={`rounded-2xl p-10 flex flex-col items-center text-center ${dark ? 'bg-white' : 'bg-brand-greenlight'}`}>
        <CheckCircle2 size={56} className="text-brand-green mb-4" />
        <h3 className="font-display font-bold text-2xl text-brand-bluedark">Thank You!</h3>
        <p className="text-slate-500 mt-2">Your message has been received. Our team will reach out shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`rounded-2xl p-6 md:p-8 space-y-4 ${dark ? 'bg-white shadow-2xl' : 'bg-white shadow-xl border border-slate-100'}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className={labelCls}>Full Name *</Label>
          <Input required value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Phone *</Label>
          <Input required value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(970) 000-0000" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className={labelCls}>Email *</Label>
          <Input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Service Needed</Label>
          <Select value={form.service} onValueChange={(v) => update('service', v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className={labelCls}>Message *</Label>
        <Textarea required value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="How can we help you?" rows={4} />
      </div>
      <Button type="submit" className="w-full bg-brand-green hover:bg-brand-greendark text-white font-semibold h-12 gap-2 text-base">
        <Send size={18} /> Send Message
      </Button>
    </form>
  );
};

export default ContactForm;
